"use client";
import { useState, useEffect, useCallback } from "react";
import { atom, useAtom } from "jotai";
const debounce = (fn: Function, ms: number) => {
  let timer: any;
  return function () {
    clearTimeout(timer);
    timer = setTimeout(function () {
      timer = null;
      //@ts-ignore
      fn.apply(this, arguments);
    }, ms);
  };
};
export const WindowWidthAtom = atom(0);

export const useWindowWidth = () => {
  const [w] = useAtom(WindowWidthAtom);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? w : 0;
};

export const useWindowDimension = () => {
  const [, setDimension] = useAtom(WindowWidthAtom);
  useEffect(() => {
    const debouncedResizeHandler = debounce(() => {
      setDimension(window?.innerWidth);
    }, 100);
    window?.addEventListener("resize", debouncedResizeHandler);
    setDimension(window?.innerWidth);
    return () => {
      window?.removeEventListener("resize", debouncedResizeHandler);
    };
  }, []);
};

function getDimensionObject(node: any) {
  const rect = node.getBoundingClientRect();

  return {
    width: rect.width,
    height: rect.height,
    top: "x" in rect ? rect.x : rect.top,
    left: "y" in rect ? rect.y : rect.left,
    x: "x" in rect ? rect.x : rect.left,
    y: "y" in rect ? rect.y : rect.top,
    right: rect.right,
    bottom: rect.bottom,
  };
}

/**
 * @param trackScroll Re-measure while scrolling. Off by default: scrolling only
 * changes an element's viewport-relative `top`/`left`, and the forced layout read
 * per scroll frame was the largest self-time function in the scroll CPU profile.
 * Opt in only if you actually consume those scroll-dependent values.
 */
export function useDimensions(
  data = null,
  liveMeasure = true,
  trackScroll = false,
) {
  const [dimensions, setDimensions] = useState<any>({});
  const [node, setNode] = useState(null);

  const ref = useCallback((node: any) => {
    setNode(node);
  }, []);

  useEffect(() => {
    if (!node) return;

    // `getDimensionObject` builds a NEW object every call, so committing it
    // unconditionally made React see a change every time and re-render the host
    // component on EVERY scroll — even when nothing had actually moved. This
    // hook is used by the header cart button, i.e. on every page, and its
    // `measure` was the single largest self-time function in the scroll CPU
    // profile. Bail out when the geometry is identical.
    const measure = () =>
      window.requestAnimationFrame(() => {
        const next = getDimensionObject(node);
        setDimensions((prev: any) => {
          if (
            prev &&
            prev.width === next.width &&
            prev.height === next.height &&
            prev.top === next.top &&
            prev.left === next.left
          ) {
            return prev; // same geometry → no state change → no re-render
          }
          return next;
        });
      });

    measure();

    if (!liveMeasure) return;

    // `resize` was previously unthrottled while only `scroll` was debounced —
    // a resize burst (mobile URL-bar show/hide, orientation change) therefore
    // ran a layout read per event.
    const handleResize = debounce(measure, 100);
    window?.addEventListener("resize", handleResize);

    const handleScroll = trackScroll ? debounce(measure, 100) : undefined;
    if (handleScroll) {
      window?.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      window?.removeEventListener("resize", handleResize);
      if (handleScroll) window?.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, data]);

  return [ref, dimensions, node];
}
